import { LegalLayout, ContactCard, type LegalSection } from './LegalLayout'
import { APP_NAME } from '@/utils/constants'

const SECTIONS: LegalSection[] = [
  { id: 'introduction', title: '1. Introduction' },
  { id: 'information-collect', title: '2. Information We Collect' },
  { id: 'guest-data', title: '3. Guests & Share-Key Access' },
  { id: 'how-use', title: '4. How We Use Your Information' },
  { id: 'sharing', title: '5. Information Sharing & Disclosure' },
  { id: 'international', title: '6. International Data Transfers' },
  { id: 'security', title: '7. Data Security' },
  { id: 'retention', title: '8. Data Retention' },
  { id: 'your-rights', title: '9. Your Privacy Rights' },
  { id: 'cookies', title: '10. Cookies & Tracking Technologies' },
  { id: 'third-party', title: '11. Third-Party Services' },
  { id: 'children', title: "12. Children's Privacy" },
  { id: 'california', title: '13. California Privacy Rights' },
  { id: 'gdpr', title: '14. GDPR Rights (EU/UK)' },
  { id: 'marketing', title: '15. Marketing Communications' },
  { id: 'changes', title: '16. Changes to Privacy Policy' },
  { id: 'contact', title: '17. Contact & Data Protection Officer' },
]

const updatedLabel = `Last updated: ${new Date().toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})}`

export function PrivacyPage() {
  return (
    <LegalLayout eyebrow='Legal' title='Privacy Policy' updatedLabel={updatedLabel} sections={SECTIONS}>
      <section id='introduction'>
        <h2>1. Introduction</h2>
        <p>
          Egowall, LLC (&ldquo;{APP_NAME},&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or
          &ldquo;us&rdquo;) is committed to protecting your privacy. This Privacy Policy explains
          how we collect, use, disclose, and safeguard personal information when you use our
          services, including our website, mobile applications (iOS and Android), API, and related
          services (collectively, the &ldquo;Services&rdquo;) — whether you're a signed-in User or
          a Guest viewing shared Content without an Account.
        </p>
        <p>
          This Privacy Policy applies to all users worldwide and complies with applicable data
          protection laws including the General Data Protection Regulation (GDPR), the California
          Consumer Privacy Act (CCPA), and other international privacy frameworks.
        </p>
        <p>
          By using our Services, you consent to the data practices described in this Privacy
          Policy. If you do not agree with this Privacy Policy, please do not use our Services.
        </p>
      </section>

      <section id='information-collect'>
        <h2>2. Information We Collect</h2>
        <h3>2.1 Personal Information You Provide</h3>
        <p>We collect information that you voluntarily provide when you:</p>
        <ul>
          <li>
            <strong>Register for an Account:</strong> name, email address, password
          </li>
          <li>
            <strong>Payment Information:</strong> billing details and payment history, processed
            securely through our third-party payment processor (Stripe) — we do not store full
            card numbers
          </li>
          <li>
            <strong>Profile Information:</strong> profile photo, display name, preferences
          </li>
          <li>
            <strong>Communications:</strong> messages, support requests, feedback
          </li>
          <li>
            <strong>Content:</strong> photos, videos, and other Content you upload to Albums and
            Galleries, plus any comments, reactions, or pins you leave on Content
          </li>
        </ul>
        <h3>2.2 Information Collected Automatically</h3>
        <p>When you use our Services, we automatically collect certain information:</p>
        <ul>
          <li>
            <strong>Device Information:</strong> IP address, browser type, operating system,
            device type
          </li>
          <li>
            <strong>Usage Data:</strong> pages viewed, features used, time spent, referring URLs
          </li>
          <li>
            <strong>Presence Data:</strong> when you open an Album or Gallery, we record a
            lightweight &ldquo;viewed&rdquo; event (topic, approximate time, and a display name)
            so hosts can see who has recently visited — this applies to Guests as well as signed-in
            Users
          </li>
          <li>
            <strong>Mobile App Data:</strong> app version, crash reports, push notification tokens
          </li>
          <li>
            <strong>Cookies &amp; Similar Technologies:</strong> session IDs and authentication
            tokens (see Section 10)
          </li>
        </ul>
        <h3>2.3 Sensitive Personal Information</h3>
        <p>
          We do not intentionally collect sensitive personal information such as racial or ethnic
          origin, political opinions, religious beliefs, health data, or biometric data. If you
          provide such information in your Content, you do so at your own discretion.
        </p>
      </section>

      <section id='guest-data'>
        <h2>3. Guests &amp; Share-Key Access</h2>
        <p>
          Many of our Services can be used without creating an Account — for example, opening an
          Album or Gallery via a share link, browsing public Discover pages, or using the guest
          pattern editor. For this kind of Guest access, we collect only what's needed to render
          the Content and, if you choose to react or comment, a display name you provide for that
          interaction.
        </p>
        <p>
          Hosts control what a Guest can see and do: a host can make an Album or Gallery private
          or public, and can revoke a share key at any time, which immediately ends Guest access
          through that key. We do not require a Guest to register just to view Content that a host
          has shared with them.
        </p>
      </section>

      <section id='how-use'>
        <h2>4. How We Use Your Information</h2>
        <p>We use your information for the following purposes:</p>
        <h3>4.1 Service Delivery</h3>
        <ul>
          <li>Provide, operate, and maintain the Services</li>
          <li>Process transactions and manage Subscriptions</li>
          <li>Authenticate Users and maintain security</li>
          <li>Store and sync your Content across devices</li>
          <li>Show hosts who has recently viewed their shared Content</li>
          <li>Provide customer support and respond to inquiries</li>
        </ul>
        <h3>4.2 Service Improvement</h3>
        <ul>
          <li>Analyze usage patterns and trends</li>
          <li>Develop new features and functionality</li>
          <li>Fix bugs and technical issues</li>
        </ul>
        <h3>4.3 Communications</h3>
        <ul>
          <li>Send service announcements and updates</li>
          <li>Provide technical notices and security alerts</li>
          <li>Send marketing communications (with your consent)</li>
        </ul>
        <h3>4.4 Security &amp; Legal Compliance</h3>
        <ul>
          <li>Detect, prevent, and investigate fraud and abuse</li>
          <li>Enforce our Terms of Service</li>
          <li>Comply with legal obligations and regulations</li>
        </ul>
        <h3>4.5 Legal Basis for Processing (GDPR)</h3>
        <p>
          We process your personal data based on: (a) performance of a contract; (b) legitimate
          interests; (c) your consent; or (d) legal obligations. You have the right to withdraw
          consent at any time.
        </p>
      </section>

      <section id='sharing'>
        <h2>5. Information Sharing &amp; Disclosure</h2>
        <h3>5.1 We Do Not Sell Your Personal Information</h3>
        <p>
          We do not sell, rent, or trade your personal information to third parties for their
          marketing purposes.
        </p>
        <h3>5.2 Service Providers</h3>
        <p>We share information with third-party service providers who perform services on our behalf:</p>
        <ul>
          <li>Cloud hosting and storage providers (AWS)</li>
          <li>Payment processing (Stripe)</li>
          <li>Email service providers</li>
          <li>Analytics providers</li>
          <li>Security and fraud prevention services</li>
        </ul>
        <p>
          These providers are contractually obligated to protect your data and use it only for
          specified purposes.
        </p>
        <h3>5.3 Visibility to Hosts &amp; Other Viewers</h3>
        <p>
          If you comment, react, or leave a pin on an Album or Gallery — as a User or as a Guest —
          that input, along with the display name you used, is visible to the host and to other
          people the host has given access to that Content. Recent-viewer presence (Section 2.2)
          is likewise visible to the host of that Album or Gallery.
        </p>
        <h3>5.4 Business Transfers</h3>
        <p>
          If we are involved in a merger, acquisition, sale of assets, bankruptcy, or
          reorganization, your information may be transferred to the successor entity. We will
          provide notice before your information becomes subject to a different privacy policy.
        </p>
        <h3>5.5 Legal Requirements</h3>
        <p>We may disclose your information if required by law or in response to valid requests by public authorities.</p>
      </section>

      <section id='international'>
        <h2>6. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your country
          of residence, including the United States, where data protection laws may differ from
          those in your country.
        </p>
        <p>
          When we transfer personal data from the European Economic Area (EEA) or UK to other
          countries, we implement appropriate safeguards including Standard Contractual Clauses
          (SCCs) approved by the European Commission and other legally recognized transfer
          mechanisms.
        </p>
      </section>

      <section id='security'>
        <h2>7. Data Security</h2>
        <p>We implement administrative, technical, and physical security measures to protect your personal information:</p>
        <ul>
          <li>
            <strong>Encryption:</strong> data in transit is encrypted using TLS/SSL; data at rest
            is encrypted using industry-standard encryption
          </li>
          <li>
            <strong>Access Controls:</strong> strict access controls and authentication mechanisms
            limit access to authorized personnel
          </li>
          <li>
            <strong>Network Security:</strong> firewalls, monitoring, and regular security audits
          </li>
          <li>
            <strong>Incident Response:</strong> documented procedures for responding to security
            incidents
          </li>
        </ul>
        <p>
          While we strive to protect your information, no security system is impenetrable. We
          cannot guarantee absolute security. If you believe your account has been compromised,
          contact us immediately at <a href='mailto:security@egowall.com'>security@egowall.com</a>.
        </p>
      </section>

      <section id='retention'>
        <h2>8. Data Retention</h2>
        <p>We retain your personal information for as long as necessary to:</p>
        <ul>
          <li>Provide the Services to you</li>
          <li>Comply with legal, tax, or accounting requirements</li>
          <li>Resolve disputes and enforce agreements</li>
          <li>Maintain security and prevent fraud</li>
        </ul>
        <p>Retention periods vary based on the type of information and purpose:</p>
        <ul>
          <li>
            <strong>Account Information:</strong> retained while your Account is active and for a
            reasonable period after account closure
          </li>
          <li>
            <strong>Transaction Records:</strong> retained for 7 years for tax and accounting
            purposes
          </li>
          <li>
            <strong>Presence / viewer data:</strong> short-lived — retained only long enough to
            power the &ldquo;recently viewed&rdquo; indicator, then aged out
          </li>
          <li>
            <strong>Marketing Data:</strong> retained until you unsubscribe or request deletion
          </li>
        </ul>
        <p>
          After the retention period, we securely delete or anonymize your information. Backups are
          deleted according to our backup retention schedule.
        </p>
      </section>

      <section id='your-rights'>
        <h2>9. Your Privacy Rights</h2>
        <p>Depending on your location, you may have the following rights:</p>
        <h3>9.1 Access &amp; Portability</h3>
        <p>
          You have the right to request access to your personal data and obtain a copy in a
          portable format.
        </p>
        <h3>9.2 Correction</h3>
        <p>
          You have the right to request correction of inaccurate or incomplete personal data. You
          can update most information directly in your Account settings.
        </p>
        <h3>9.3 Deletion</h3>
        <p>
          You have the right to request deletion of your personal data, subject to certain
          exceptions (e.g., legal obligations, fraud prevention).
        </p>
        <h3>9.4 Restriction &amp; Objection</h3>
        <p>
          You have the right to restrict processing or object to certain types of processing,
          including direct marketing.
        </p>
        <h3>9.5 How to Exercise Your Rights</h3>
        <p>
          To exercise your rights, contact us at{' '}
          <a href='mailto:privacy@egowall.com'>privacy@egowall.com</a> or use the privacy controls
          in your Account settings. We will respond within 30 days (or as required by applicable
          law).
        </p>
      </section>

      <section id='cookies'>
        <h2>10. Cookies &amp; Tracking Technologies</h2>
        <h3>10.1 What Are Cookies</h3>
        <p>
          Cookies are small text files stored on your device. We use cookies and similar
          technologies (local storage, session tokens) to keep you signed in and remember your
          preferences.
        </p>
        <h3>10.2 Types of Cookies We Use</h3>
        <ul>
          <li>
            <strong>Essential Cookies:</strong> required for the Services to function
            (authentication, security)
          </li>
          <li>
            <strong>Functional Cookies:</strong> remember your preferences and settings
          </li>
          <li>
            <strong>Analytics Cookies:</strong> help us understand how you use the Services
          </li>
        </ul>
        <h3>10.3 Cookie Management</h3>
        <p>
          You can control cookies through your browser settings. Note that disabling essential
          cookies may affect the functionality of the Services.
        </p>
      </section>

      <section id='third-party'>
        <h2>11. Third-Party Services</h2>
        <p>
          Our Services may contain links to third-party websites or integrate with third-party
          services. We are not responsible for the privacy practices of these third parties. We
          encourage you to review their privacy policies.
        </p>
      </section>

      <section id='children'>
        <h2>12. Children's Privacy</h2>
        <p>
          Our Services are not directed to children under 13 years old (or 16 in some
          jurisdictions). We do not knowingly collect personal information from children under
          these ages.
        </p>
        <p>
          If you are a parent or guardian and believe your child has provided us with personal
          information, contact us at{' '}
          <a href='mailto:privacy@egowall.com'>privacy@egowall.com</a> and we will delete such
          information from our systems.
        </p>
      </section>

      <section id='california'>
        <h2>13. California Privacy Rights (CCPA)</h2>
        <p>
          If you are a California resident, you have specific rights under the California Consumer
          Privacy Act (CCPA), including the right to know what personal information we collect, the
          right to delete it, and the right to opt out of its sale — we do not sell personal
          information, so there is nothing to opt out of. We will not discriminate against you for
          exercising your CCPA rights.
        </p>
      </section>

      <section id='gdpr'>
        <h2>14. GDPR Rights (EU/UK Users)</h2>
        <p>
          If you are located in the European Economic Area (EEA) or United Kingdom, you have
          specific rights under the General Data Protection Regulation (GDPR), including rights of
          access, rectification, erasure, restriction, portability, objection, and the right to
          withdraw consent at any time. To exercise these rights, contact our Data Protection
          Officer at <a href='mailto:dpo@egowall.com'>dpo@egowall.com</a>.
        </p>
      </section>

      <section id='marketing'>
        <h2>15. Marketing Communications</h2>
        <p>
          With your consent, we may send you marketing communications about our Services,
          features, and promotions. You can opt out at any time by clicking &ldquo;unsubscribe&rdquo;
          in any marketing email, adjusting email preferences in your Account settings, or
          contacting us at <a href='mailto:marketing@egowall.com'>marketing@egowall.com</a>. Note
          that you cannot opt out of transactional emails necessary for the Services (e.g., account
          notifications, security alerts).
        </p>
      </section>

      <section id='changes'>
        <h2>16. Changes to Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices,
          technology, or legal requirements. We will notify you of material changes by email, a
          prominent notice on our Services, or by updating the &ldquo;Last updated&rdquo; date at
          the top of this policy.
        </p>
        <p>
          Your continued use of the Services after notice of changes constitutes your acceptance of
          the updated Privacy Policy.
        </p>
      </section>

      <section id='contact'>
        <h2>17. Contact &amp; Data Protection Officer</h2>
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or our data
          practices, please contact us:
        </p>
        <ContactCard>
          <p>
            <strong>General Privacy Inquiries:</strong>
            <br />
            <a href='mailto:privacy@egowall.com'>privacy@egowall.com</a>
          </p>
          <p>
            <strong>Data Protection Officer (DPO):</strong>
            <br />
            <a href='mailto:dpo@egowall.com'>dpo@egowall.com</a>
          </p>
          <p>
            <strong>Security Issues:</strong>
            <br />
            <a href='mailto:security@egowall.com'>security@egowall.com</a>
          </p>
          <p>
            <strong>Mailing Address:</strong>
            <br />
            Egowall, LLC
            <br />
            Attn: Terms and Conditions Issues
            <br />
            3102 Oak Lawn Avenue, Suite 505
            <br />
            Dallas, TX 75219, USA
          </p>
        </ContactCard>
      </section>
    </LegalLayout>
  )
}
